import { ContentCompletionByMonth } from "@coaching-culture/types";
import { CheckBox, EmptyState, Flex, Panel, Text } from "@coaching-culture/ui";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { addMonths, differenceInMonths, format } from "date-fns";
import { minBy, range } from "lodash";
import { useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import { FaChartLine } from "react-icons/fa";
import styled, { useTheme } from "styled-components";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
);

type CompletionsWidgetProps = {
  completionData: ContentCompletionByMonth[];
};

/**
 * Categorical series colours, in fixed order — never cycled, and never
 * reassigned by rank, so a type keeps its colour as the data changes.
 *
 * Validated as a set on a white chart surface: every slot clears 3:1 contrast,
 * worst adjacent colour-blind separation is ΔE 9.0 (protan) and worst
 * normal-vision separation ΔE 19.1, both above the thresholds. Changing one of
 * these means re-validating all four together.
 */
const SERIES = [
  { label: "Mindset", color: "#3987e5" }, // design-lint-ignore: validated chart palette
  { label: "Lessons", color: "#d95926" }, // design-lint-ignore: validated chart palette
  { label: "Assessments", color: "#199e70" }, // design-lint-ignore: validated chart palette
  { label: "LearnCast", color: "#c07e00" }, // design-lint-ignore: validated chart palette
] as const;

const TYPES = ["module", "lesson", "assessment", "learncast"] as const;

/**
 * The legend swatch. Big enough to read the colour at a glance, which a 2px
 * line sample is not, and it sits next to the checkbox that switches the
 * series rather than being the control itself.
 */
const Swatch = styled.span<{ color: string; on: boolean }>`
  width: 22px;
  height: 5px;
  flex: none;
  border-radius: 3px;
  margin-right: 8px;
  background: ${(p) => p.color};
  opacity: ${(p) => (p.on ? 1 : 0.25)};
  transition: opacity 0.15s ease;
`;

const SeriesName = styled.span<{ on: boolean }>`
  color: ${(p) => (p.on ? p.theme.colors.text : p.theme.colors.textMuted)};
  white-space: nowrap;
`;

const compact = new Intl.NumberFormat("en-GB", {
  notation: "compact",
  maximumFractionDigits: 1,
});
const full = new Intl.NumberFormat("en-GB");

export const CompletionsWidget = ({
  completionData,
}: CompletionsWidgetProps) => {
  const theme = useTheme();
  /** Series switched off in the legend. Hiding the big one rescales the axis. */
  const [hidden, setHidden] = useState<string[]>([]);

  const { labels, series } = useMemo(() => {
    const all = completionData ?? [];
    const earliest = minBy(all, (x) => x.year * 12 + x.month);

    if (earliest == null) {
      return { labels: [] as string[], series: [] as number[][] };
    }

    const earliestDate = new Date(earliest.year, earliest.month - 1, 1);
    // Inclusive of both ends, so a single month of data still plots.
    const months = range(differenceInMonths(new Date(), earliestDate) + 1)
      .map((x) => addMonths(new Date(), -x))
      .reverse();

    /** Everything of this type completed up to and including this month. */
    const cumulative = (type: string, at: Date) => {
      const limit = at.getFullYear() * 12 + (at.getMonth() + 1);
      return all
        .filter((x) => x.type === type && x.year * 12 + x.month <= limit)
        .reduce((total, x) => total + x.count, 0);
    };

    return {
      labels: months.map((x) => format(x, "MMM yy")),
      series: TYPES.map((type) => months.map((at) => cumulative(type, at))),
    };
  }, [completionData]);

  // Rebuilding these on every render makes Chart.js re-animate and forget
  // which series the viewer has toggled off in the legend.
  const data = useMemo(
    () => ({
      labels,
      datasets: SERIES.map((s, i) => ({
        label: s.label,
        data: series[i] ?? [],
        hidden: hidden.includes(s.label),
        borderColor: s.color,
        backgroundColor: s.color,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 5,
        // The 2px ring is the surface colour, so overlapping points stay legible.
        pointHoverBorderWidth: 2,
        pointHoverBorderColor: theme.colors.surface,
        pointHoverBackgroundColor: s.color,
        // Straight segments: these totals only ever rise, and curve smoothing
        // overshoots into dips that the data can't contain.
        tension: 0,
      })),
    }),
    [labels, series, hidden, theme.colors.surface],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      // The whole month is one reading: hovering anywhere gives every series.
      interaction: { mode: "index" as const, intersect: false },
      layout: { padding: { top: 8, right: 8 } },
      scales: {
        x: {
          grid: { display: false },
          border: { color: theme.colors.border },
          ticks: {
            color: theme.colors.textMuted,
            maxRotation: 0,
            autoSkipPadding: 16,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: theme.colors.border,
            drawTicks: false,
          },
          border: { display: false },
          ticks: {
            color: theme.colors.textMuted,
            padding: 8,
            maxTicksLimit: 6,
            // Completions are whole things; no 0.5 of one.
            precision: 0,
            callback: (value: number | string) => compact.format(Number(value)),
          },
        },
      },
      plugins: {
        // The legend is real checkboxes above the chart, so it reads as
        // something you can switch and is reachable from the keyboard.
        legend: { display: false },
        tooltip: {
          backgroundColor: theme.colors.text,
          padding: 10,
          cornerRadius: 6,
          usePointStyle: true,
          boxPadding: 4,
          callbacks: {
            label: (ctx: any) =>
              ` ${ctx.dataset.label}: ${full.format(ctx.parsed.y)}`,
          },
        },
      },
    }),
    [theme.colors.border, theme.colors.textMuted, theme.colors.text],
  );

  return (
    <>
      <Text fontSize="section" fontWeight={600} color="black" mb={2}>
        Completions
      </Text>
      <Panel mb={5} p={3}>
        {labels.length === 0 ? (
          <EmptyState icon={FaChartLine} title="Nothing completed yet">
            Completed mindsets, lessons, assessments and LearnCasts will be
            charted here by month.
          </EmptyState>
        ) : (
          <>
            <Flex
              as="fieldset"
              flexWrap="wrap"
              alignItems="center"
              mb={2}
              style={{ gap: "8px 24px", border: 0, margin: 0, padding: 0 }}
            >
              <Text as="legend" fontSize="small" color="textMuted" mr={2}>
                Show
              </Text>
              {SERIES.map((s) => {
                const on = !hidden.includes(s.label);
                return (
                  <CheckBox
                    key={s.label}
                    value={on}
                    onChange={() =>
                      setHidden((h) =>
                        on ? h.concat(s.label) : h.filter((x) => x !== s.label),
                      )
                    }
                    label={
                      <>
                        <Swatch color={s.color} on={on} aria-hidden="true" />
                        <SeriesName on={on}>{s.label}</SeriesName>
                      </>
                    }
                  />
                );
              })}
            </Flex>
            {/* Fixed height, with the axis band inside it rather than clipped. */}
            <div style={{ height: 320 }}>
              <Line data={data} options={options as any} />
            </div>
          </>
        )}
      </Panel>
    </>
  );
};
